import type { PublicTableOption } from "@/hooks/usePublicShow";
import type { ShowTableRequest } from "@/hooks/useShowOperations";
import { formatShowMoney } from "@/lib/shows/showOperations";

/** A Vendor's own Table requests on one Show, split by what each one still asks of whom. */
export type TableApplicationStanding = {
  /** Applications the Organizer has not answered. Submitting the same one again returns it. */
  awaiting: ShowTableRequest[];
  /** Tables this Vendor now holds. */
  approved: ShowTableRequest[];
  /** Offers the Organizer made. They are answered from the Vendor's own Shows list, not here. */
  offered: ShowTableRequest[];
  /** Requests that ended with no Table, so a declined or released one still says so. */
  closed: ShowTableRequest[];
};

/**
 * Splits a Vendor's Table requests on one Show into the four positions it can be in.
 *
 * An invitation is kept apart from an application rather than folded in with it: the two look
 * alike on the wire and are answered in opposite directions, so a screen that offers to withdraw
 * would be offering to withdraw the Organizer's offer.
 */
export function tableApplicationStanding(
  requests: readonly ShowTableRequest[] = [],
): TableApplicationStanding {
  const standing: TableApplicationStanding = {
    awaiting: [],
    approved: [],
    offered: [],
    closed: [],
  };
  for (const request of requests) {
    if (request.status === "awaiting_organizer") standing.awaiting.push(request);
    else if (request.status === "approved") standing.approved.push(request);
    else if (request.status === "awaiting_vendor") standing.offered.push(request);
    else standing.closed.push(request);
  }
  return standing;
}

/**
 * Whether a Vendor may take this Table request back.
 *
 * Only its own unanswered application: the server also lets a Vendor withdraw an invitation, but
 * turning down an offer is declining it, and that lives with the offer.
 */
export function isWithdrawable(request: Pick<ShowTableRequest, "status">): boolean {
  return request.status === "awaiting_organizer";
}

/** What applying for `quantity` of one Table option costs, in the option's own currency. */
export function applicationTotal(
  option: Pick<PublicTableOption, "unit_fee" | "currency">,
  quantity: number,
): string {
  // In whole cents, so a fee carrying them does not accumulate a float remainder per table.
  const cents = Math.round(Number(option.unit_fee) * 100) * quantity;
  return formatShowMoney((cents / 100).toFixed(2), option.currency);
}

/**
 * The days one Table option covers, or "" when the Show runs a single session.
 *
 * Carries the date rather than the weekday alone: a Show can sit on two Saturdays, and an option
 * covering one of them has to say which.
 */
export function tableOptionDaysLabel(
  option: Pick<PublicTableOption, "session_ids">,
  sessions: readonly { public_id: string; starts_at: string; }[],
  timeZone: string,
): string {
  if (sessions.length < 2) return "";
  const format = (zone: string, startsAt: string) =>
    new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      weekday: "long",
      month: "short",
      day: "numeric",
    }).format(new Date(startsAt));
  const day = (startsAt: string) => {
    try {
      return format(timeZone || "UTC", startsAt);
    } catch {
      return format("UTC", startsAt);
    }
  };
  const byId = new Map(sessions.map((session) => [session.public_id, session.starts_at]));
  return option.session_ids
    .flatMap((sessionId) => {
      const startsAt = byId.get(sessionId);
      return startsAt ? [day(startsAt)] : [];
    })
    .join(" · ");
}
